/**
 * <s-email-popup> — opens the email signup dialog once per visitor.
 *
 * - Waits `data-delay` ms, then calls showModal() unless the visitor dismissed it
 *   in the last 14 days or already subscribed (Shopify adds customer_posted=true).
 * - Never auto-opens in the theme editor; selecting the block there previews it.
 */
const STORAGE_KEY = "s4a-email-popup-dismissed";
const SUPPRESS_MS = 14 * 24 * 60 * 60 * 1000;

class SEmailPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector(`#${this.dataset.dialog}`);
    if (!this.dialog) return;

    this.onClose = () => this.remember();
    this.onBackdrop = (event) => {
      if (event.target === this.dialog) this.dialog.close();
    };
    this.onSelect = (event) => {
      if (this.contains(event.target) && !this.dialog.open) this.dialog.showModal();
    };
    this.onDeselect = (event) => {
      if (this.contains(event.target) && this.dialog.open) this.dialog.close();
    };

    this.dialog.addEventListener("close", this.onClose);
    this.dialog.addEventListener("click", this.onBackdrop);

    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:block:select", this.onSelect);
      document.addEventListener("shopify:block:deselect", this.onDeselect);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get("customer_posted") === "true") {
      this.remember();
      return;
    }
    if (this.recentlyDismissed()) return;

    const delay = Number(this.dataset.delay) || 0;
    this.timer = window.setTimeout(() => {
      if (!this.dialog.open && !document.querySelector("dialog[open]")) this.dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.dialog?.removeEventListener("close", this.onClose);
    this.dialog?.removeEventListener("click", this.onBackdrop);
    document.removeEventListener("shopify:block:select", this.onSelect);
    document.removeEventListener("shopify:block:deselect", this.onDeselect);
  }

  recentlyDismissed() {
    try {
      const stamp = Number(window.localStorage.getItem(STORAGE_KEY));
      return Boolean(stamp) && Date.now() - stamp < SUPPRESS_MS;
    } catch {
      return false;
    }
  }

  remember() {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* storage unavailable: popup may show again next visit */
    }
  }
}

if (!customElements.get("s-email-popup")) customElements.define("s-email-popup", SEmailPopup);
